import { type ChangeEvent, type ReactNode, useCallback, useMemo, useState } from "react";
import { toMapFn } from "~/common/utils/reducers";
import { TextWithHighlight } from "../TextWithHighlight";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";
import { SimpleSelectItem } from "./SimpleSelect";
import { Autocomplete, TextField, type AutocompleteProps, type TextFieldProps } from "~/common/mui";

interface Props<FreeSolo extends boolean | undefined>
  extends Omit<
    AutocompleteProps<string, false, boolean, FreeSolo>,
    "options" | "renderOption" | "onChange" | "value" | "multiple" | "loadingText" | "renderInput"
  > {
  values: DropdownEntry<string | number>[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  error?: boolean;
  helperText?: ReactNode;
  inputProps?: TextFieldProps["inputProps"];
}

type OnChangeType = AutocompleteProps<string, false, boolean, undefined>["onChange"];

export const SimpleAutocomplete = <FreeSolo extends boolean | undefined = undefined>({
  values,
  value: valueProp,
  onInputChange,
  onChange: onChangeProp,
  label,
  ...rest
}: Props<FreeSolo> & { label?: ReactNode }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(valueProp ?? rest.defaultValue);

  const { options, optionMap } = useOptions(values, (values) => values.stringLabel ?? "");
  const { optionMap: valueMap } = useOptions(values, (values) => values.value ?? "");

  const onChange: OnChangeType = useCallback(
    (_: ChangeEvent<unknown>, label: string | null) => {
      const val = optionMap[label!].value;
      onChangeProp?.(val);
    },
    [onChangeProp, optionMap],
  );

  const value = valueMap[valueProp ?? ""]?.stringLabel ?? "";

  return (
    <Autocomplete
      loadingText={t("spinner.text")}
      disableClearable={true}
      autoHighlight={true}
      value={value}
      getOptionLabel={(value) => {
        return optionMap[value]?.stringLabel || "";
      }}
      onInputChange={(e, value, reason) => {
        setInputValue(value);
        onInputChange?.(e, value, reason);
      }}
      options={options}
      renderOption={(props, value) => {
        return (
          <SimpleSelectItem {...props}>
            <TextWithHighlight highlight={inputValue?.toString() ?? ""}>{value}</TextWithHighlight>
          </SimpleSelectItem>
        );
      }}
      onChange={onChange}
      {...rest}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoComplete="off"
          error={rest.error}
          helperText={rest.helperText}
          inputProps={{ ...params.inputProps, ...rest.inputProps }}
        />
      )}
    />
  );
};

export function useOptions<T extends object>(values: T[], keyFunc: (obj: T) => string | number) {
  return useMemo(() => {
    const optionMap = toMapFn(values, keyFunc);

    return {
      options: Object.keys(optionMap),
      optionMap,
    };
  }, [values, keyFunc]);
}
